import type { DishKind } from "../game/dishes";

// Each dish drawn in a 48×48 box, with its grime as blobs on top: one blob
// per scrub left, so you watch it come clean.

interface Spot {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  className: string;
}

const SPOTS: Record<DishKind, Spot[]> = {
  plate: [
    { cx: 19, cy: 21, rx: 6, ry: 5, className: "fill-yolk" },
    { cx: 29, cy: 28, rx: 5, ry: 3.5, className: "fill-coffee" },
  ],
  mug: [{ cx: 21, cy: 14, rx: 9, ry: 2.5, className: "fill-coffee" }],
  bowl: [
    { cx: 15, cy: 26, rx: 4.5, ry: 3.5, className: "fill-oat" },
    { cx: 26, cy: 30, rx: 5.5, ry: 4, className: "fill-oat" },
    { cx: 33, cy: 24, rx: 3.5, ry: 3, className: "fill-oat" },
  ],
  basket: [
    { cx: 17, cy: 20, rx: 5, ry: 2.5, className: "fill-coffee" },
    { cx: 30, cy: 21, rx: 4.5, ry: 2, className: "fill-yolk" },
  ],
  cup: [{ cx: 24, cy: 18, rx: 10, ry: 2.5, className: "fill-tea" }],
  tumbler: [{ cx: 24, cy: 34, rx: 7, ry: 2.5, className: "fill-coffee" }],
  shotglass: [{ cx: 24, cy: 34, rx: 6, ry: 2, className: "fill-coffee" }],
  pitcher: [{ cx: 22, cy: 30, rx: 8, ry: 2.5, className: "fill-oat" }],
  sauce: [{ cx: 24, cy: 21, rx: 13, ry: 3.5, className: "fill-coffee" }],
  wok: [
    { cx: 16, cy: 26, rx: 5, ry: 2.5, className: "fill-coffee" },
    { cx: 26, cy: 29, rx: 5, ry: 2.5, className: "fill-chili" },
    { cx: 33, cy: 25, rx: 4, ry: 2, className: "fill-coffee" },
    { cx: 22, cy: 23, rx: 4, ry: 2, className: "fill-oat" },
  ],
};

export function DishIcon({ kind, grime = 0, size = 40 }: { kind: DishKind; grime?: number; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" className="shrink-0">
      {kind === "plate" && (
        <>
          <circle cx="24" cy="24" r="21" className="fill-panel stroke-steel-dark" strokeWidth="2" />
          <circle cx="24" cy="24" r="13" className="fill-none stroke-steel" strokeWidth="2" />
        </>
      )}
      {kind === "mug" && (
        <>
          <path d="M32 18h4a6 6 0 0 1 0 12h-4" className="fill-none stroke-steel-dark" strokeWidth="3" />
          <rect x="10" y="12" width="22" height="26" rx="4" className="fill-accent-soft stroke-steel-dark" strokeWidth="2" />
          <ellipse cx="21" cy="14" rx="10" ry="2.5" className="fill-panel stroke-steel-dark" strokeWidth="1.5" />
        </>
      )}
      {kind === "bowl" && (
        <>
          <rect x="17" y="36" width="14" height="4" rx="1.5" className="fill-steel-dark" />
          <path d="M5 20h38a19 17 0 0 1-38 0z" className="fill-sunny-soft stroke-steel-dark" strokeWidth="2" />
          <ellipse cx="24" cy="20" rx="19" ry="3" className="fill-panel stroke-steel-dark" strokeWidth="1.5" />
        </>
      )}
      {kind === "basket" && (
        <>
          <path d="M4 14h40l-6 22H10z" className="fill-chili stroke-ink" strokeWidth="1.5" />
          <path d="M8 17h32l-1.5 5H9.5z" className="fill-panel" />
        </>
      )}
      {kind === "cup" && (
        <>
          <path d="M11 18h26l-4 18a4 4 0 0 1-4 3H19a4 4 0 0 1-4-3z" className="fill-panel stroke-steel-dark" strokeWidth="2" />
          <ellipse cx="24" cy="18" rx="13" ry="3" className="fill-tile stroke-steel-dark" strokeWidth="1.5" />
        </>
      )}
      {kind === "tumbler" && (
        <>
          <path d="M13 8h22l-3 32H16z" className="fill-panel stroke-steel-dark" strokeWidth="2" />
          <ellipse cx="24" cy="8" rx="11" ry="2.5" className="fill-tile stroke-steel-dark" strokeWidth="1.5" />
        </>
      )}
      {kind === "shotglass" && (
        <>
          <path d="M16 14h16l-2 24H18z" className="fill-tile stroke-steel-dark" strokeWidth="2" />
          <ellipse cx="24" cy="14" rx="8" ry="2" className="fill-panel stroke-steel-dark" strokeWidth="1.5" />
        </>
      )}
      {kind === "pitcher" && (
        <>
          <path d="M33 18q8 2 6 12" className="fill-none stroke-steel-dark" strokeWidth="3" />
          <path d="M14 10h18l2 30H12z" className="fill-steel stroke-steel-dark" strokeWidth="2" />
          <path d="M14 10l-4-3" className="stroke-steel-dark" strokeWidth="2" />
        </>
      )}
      {kind === "sauce" && (
        <>
          <path d="M4 20h40l-6 13H10z" className="fill-porcelain stroke-steel-dark" strokeWidth="2" />
          <ellipse cx="24" cy="20" rx="20" ry="6" className="fill-panel stroke-steel-dark" strokeWidth="1.5" />
        </>
      )}
      {kind === "wok" && (
        <>
          <path d="M33 22l13-6" className="stroke-wood" strokeWidth="4" strokeLinecap="round" />
          <path d="M3 22h34a17 13 0 0 1-34 0z" className="fill-iron stroke-ink" strokeWidth="1.5" />
          <ellipse cx="20" cy="22" rx="17" ry="4" className="fill-steel-dark stroke-ink" strokeWidth="1.5" />
        </>
      )}
      {SPOTS[kind].slice(0, grime).map((s, i) => (
        <ellipse key={i} cx={s.cx} cy={s.cy} rx={s.rx} ry={s.ry} className={s.className} />
      ))}
    </svg>
  );
}
